import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trialProspectsApi, type TrialStatus } from "@/services/edgeFunctionWrappers/trialProspectsWrapper";
import { getUserGymId } from "@/utils/gymAuth";
import { useUserStore } from "@/store/userStore";

interface ListFilters {
  status?: TrialStatus;
  start_date?: string;
  end_date?: string;
  search?: string;
}

export function useTrialProspects(filters: ListFilters = {}) {
  const gymId = useUserStore(s => s.gymId);
  return useQuery({
    queryKey: ["trial-prospects", gymId, filters],
    queryFn: () => trialProspectsApi.list(gymId!, filters),
    enabled: !!gymId,
  });
}

export function useTrialStats(range: { start_date?: string; end_date?: string } = {}) {
  const gymId = useUserStore(s => s.gymId);
  return useQuery({
    queryKey: ["trial-stats", gymId, range],
    queryFn: () => trialProspectsApi.stats(gymId!, range),
    enabled: !!gymId,
  });
}

export function useTrialProspectMutations() {
  const qc = useQueryClient();
  const gymId = getUserGymId();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["trial-prospects"] });
    qc.invalidateQueries({ queryKey: ["trial-stats"] });
    qc.invalidateQueries({ queryKey: ["schedule"] });
    qc.invalidateQueries({ queryKey: ["scheduleClasses"] });
    // ✅ Vraie clé utilisée par useScheduleClasses (planning hebdo)
    qc.invalidateQueries({ queryKey: ["schedule-classes"] });
  };

  const create = useMutation({
    mutationFn: (p: Parameters<typeof trialProspectsApi.create>[1]) =>
      trialProspectsApi.create(gymId!, p),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (p: Parameters<typeof trialProspectsApi.update>[1]) =>
      trialProspectsApi.update(gymId!, p),
    onSuccess: invalidate,
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TrialStatus }) =>
      trialProspectsApi.updateStatus(gymId!, id, status),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => trialProspectsApi.remove(gymId!, id),
    onSuccess: invalidate,
  });
  const convert = useMutation({
    mutationFn: ({ id, subscription_type_id, app_name }: { id: string; subscription_type_id?: string; app_name?: string }) =>
      trialProspectsApi.convert(gymId!, id, { subscription_type_id, app_name }),
    onSuccess: invalidate,
  });
  const cancelTrial = useMutation({
    mutationFn: (id: string) => trialProspectsApi.cancel(gymId!, id),
    onSuccess: invalidate,
  });
  const rescheduleTrial = useMutation({
    mutationFn: ({ id, new_class_id, new_class_date }: { id: string; new_class_id: string; new_class_date: string }) =>
      trialProspectsApi.reschedule(gymId!, id, new_class_id, new_class_date),
    onSuccess: invalidate,
  });

  return { create, update, updateStatus, remove, convert, cancelTrial, rescheduleTrial };
}

